import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cardSpecs, panelFor } from "@/lib/c360Cards";
import { comma, num1, pct } from "@/lib/c360Profile";
import { short } from "@/lib/creator360";
import { Gauge, TwoBars, Waffle, WeekSquares } from "@/components/c360/profile/Charts";
import { PromoTimeline } from "@/components/c360/profile/YouTubeExtras";

const BANDS = { ig: { u20k: "under 20K", "20k": "20K–100K", "100k": "100K–500K", "500k": "500K+" }, tg: { u2k: "under 2K", "2k": "2K–10K", "10k": "10K–50K", "50k": "50K+" } };
const ALL = { yt: "All YouTube creators", live: "All live streamers", ig: "All Instagram creators", tg: "All Telegram creators" };
function ownLabel(av, d) {
  const g = d.groups[av].own;
  if (av === "yt" || av === "live") return `Same tier as them (Tier ${g})`;
  return `Similar size to them (${BANDS[av][g]} ${av === "ig" ? "followers" : "subscribers"})`;
}

function ExPicker({ opts, value, onChange }) {
  const label = value.length ? (value.length === 1 ? opts.find((o) => o.key === value[0])?.name : `${value.length} exchanges`) : "All exchanges";
  const flip = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="h-9 px-3 rounded-md border hairline text-sm text-zinc-200 inline-flex items-center gap-2" style={{ background: "var(--surface)" }} data-testid="c360-cmp-ex">{label}<ChevronDown size={13} className="text-zinc-500" /></button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5 border hairline max-h-80 overflow-y-auto" style={{ background: "var(--surface-2)" }}>
        <button type="button" onClick={() => onChange([])} className="w-full flex items-center justify-between px-2 py-1.5 rounded text-sm text-zinc-200 hover:bg-white/5" data-testid="c360-cmp-ex-all">All exchanges {!value.length && <Check size={13} className="text-cyan-400" />}</button>
        {opts.map((o) => (
          <button key={o.key} type="button" onClick={() => flip(o.key)} className="w-full flex items-center justify-between px-2 py-1.5 rounded text-sm text-zinc-300 hover:bg-white/5" data-testid={`c360-cmp-ex-${o.key.replace(/\s+/g, "-")}`}>
            <span className="inline-flex items-center gap-2"><span className={`w-3.5 h-3.5 rounded-sm border ${value.includes(o.key) ? "bg-cyan-400 border-cyan-400" : "border-zinc-600"}`} />{o.name}</span>
            <span className="mono text-[11px] text-zinc-500">{o.n.toLocaleString()}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function useTypical(av, d, pick, ex) {
  const base = pick === "all" ? d.groups[av].all : d.groups[av].own_t;
  const [got, setGot] = useState(null);
  const key = `${av}|${pick}|${ex.join(",")}`;
  useEffect(() => {
    setGot(null);
    if (!ex.length) return undefined;
    let alive = true;
    api.get("/creator360/typical", { params: { av, group: pick === "all" ? "all" : d.groups[av].own, ex: ex.join(",") } })
      .then((r) => { if (alive) setGot({ key, t: r.data }); }).catch(() => {});
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return ex.length ? (got?.key === key ? got.t : null) : base;
}

const FMT = { pct, num: num1, short, comma, chat: (v) => `${num1(v)}/min`, hours: (v) => `${Math.round(v)} hrs` };

function Card({ c, on, onPick, av }) {
  return (
    <button type="button" onClick={onPick} data-testid={`c360-card-${av}-${c.k}`} data-on={on ? "1" : "0"}
      className="text-left rounded-lg border p-3.5 transition-transform duration-150 hover:-translate-y-0.5 min-w-0"
      style={{ borderColor: on ? "#00F0FF" : "rgba(255,255,255,0.08)", background: on ? "rgba(0,240,255,0.05)" : "var(--surface)" }}>
      <div className="flex items-center justify-between gap-2"><span className="text-[11px] text-zinc-400 truncate">{c.name}</span>{on && <span className="mono text-[9px] tracking-[0.2em] text-cyan-400">SHOWING</span>}</div>
      <div className="text-xl font-semibold text-white mt-1 truncate">{c.big}</div>
      <div className="text-[11px] text-zinc-500 leading-snug mt-1 line-clamp-3">{c.sub}</div>
      {c.meter != null && <div className="h-1 rounded-full mt-2" style={{ background: "rgba(255,255,255,0.08)" }}><div className="h-full rounded-full" style={{ width: `${Math.min(100, c.meter)}%`, background: "#00F0FF" }} /></div>}
    </button>
  );
}

const TAG_COLOR = { "verdict: strong": "#39FF14", "verdict: good": "#FBBF24", "verdict: weak": "#F43F5E", active: "#39FF14", inactive: "#F43F5E" };

function Panel({ c, av, d, pick, typ }) {
  if (c.chart === "timeline") return <PromoTimeline d={d} />;
  const p = panelFor(c, av, d, pick, typ);
  const tagColor = TAG_COLOR[(p.tag || "").toLowerCase()] || "#A1A1AA";
  return (
    <div className="rounded-lg border hairline p-5 c360-rise" style={{ background: "var(--surface)" }} data-testid={`c360-panel-${av}-${c.k}`}>
      {p.missing ? <p className="text-sm text-zinc-400" data-testid="c360-panel-missing">{p.missing}</p> : (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-base md:text-lg text-white font-medium" data-testid="c360-panel-title">{p.title}</h3>
              {p.tag && <span className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded" style={{ color: tagColor, background: `${tagColor}18` }} data-testid="c360-panel-tag">{p.tag}</span>}
            </div>
            <div className="mt-5">
              {c.chart === "waffle" && <Waffle them={c.them} typ={p.t} />}
              {c.chart === "gauge" && <Gauge them={c.them} typ={p.t} />}
              {c.chart === "weeks" && <WeekSquares them={c.them} typ={p.t} />}
              {c.chart === "bars" && <TwoBars rows={p.bars || [["Them", c.them], [`Typical ${p.vg}`, p.t]]} fmt={(v) => (c.f ? FMT[c.f](v) : short(v))} />}
            </div>
            {(c.cap || p.cap) && <p className="mono text-[10px] text-zinc-500 mt-3" data-testid="c360-panel-cap">{p.cap || c.cap}</p>}
            {p.line && <p className="text-sm text-zinc-300 mt-3" data-testid="c360-panel-line">{p.line}</p>}
            {p.clash && <p className="text-xs text-amber-300/80 mt-2" data-testid="c360-panel-clash">{p.clash}</p>}
            {c.note && <p className="text-xs text-zinc-500 mt-2">{c.note}</p>}
            {c.k === "chat" && tilesNote(d) && <p className="text-xs text-amber-300/80 mt-2" data-testid="c360-panel-chatnote">{tilesNote(d)}</p>}
          </div>
          <div className="lg:w-60 space-y-3" data-testid="c360-panel-facts">
            {(p.facts || []).map(([k, v, cy]) => <div key={k}><div className="text-[11px] text-zinc-500">{k}</div><div className={`text-sm ${cy ? "text-cyan-300 font-semibold" : "text-zinc-200"}`}>{v}</div></div>)}
          </div>
        </div>
      )}
      {c.why && <div className="mt-5 rounded-md p-3 text-xs text-zinc-400" style={{ background: "rgba(255,255,255,0.03)" }} data-testid="c360-panel-why"><span className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mr-2">Why it matters</span>{c.why}</div>}
    </div>
  );
}

function tilesNote(d) {
  const n = d.nums.live;
  const g = d.av.live.checks.chat?.grade;
  return n.chat != null && n.chat < 5 && (g === "good" || g === "strong") ? "Good to know: a lively live chat usually runs at 5+ messages a minute. Worth a quick look at a recent stream to check their chat engagement is up to the mark." : null;
}

export function ComparePanel({ av, d }) {
  const [pick, setPick] = useState(av === "live" || !d.groups[av].own ? "all" : "own");
  const [ex, setEx] = useState([]);
  const cards = useMemo(() => cardSpecs(av, d), [av, d]);
  const [sel, setSel] = useState(cards[0].k);
  const typ = useTypical(av, d, pick, ex);
  const card = cards.find((c) => c.k === sel) || cards[0];
  const opts = (pick === "all" ? d.groups[av].all : d.groups[av].own_t)?.ex || [];
  const ownYt = card.src === "own_yt";
  const count = ownYt ? `${card.who[0].toUpperCase() + card.who.slice(1)} are compared with their own YouTube` : typ ? (av === "live" ? `${typ.n.toLocaleString()} live streamers on Creator 360 in this comparison` : `${typ.active.toLocaleString()} active creators on Creator 360 in this comparison`) : "Working out…";
  const parts = av === "live" ? [["during", "During the stream"], ["week", "Every week"]] : [[undefined, null]];
  return (
    <section className="space-y-4" data-testid={`c360-compare-${av}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-1">Compare them with</span>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="h-9 w-auto min-w-[220px] border hairline text-sm" style={{ background: "var(--surface)" }} data-testid="c360-cmp-group"><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: "var(--surface-2)" }}>
              <SelectItem value="all" data-testid="c360-cmp-group-all">{ALL[av]}</SelectItem>
              {d.groups[av].own && <SelectItem value="own" data-testid="c360-cmp-group-own">{ownLabel(av, d)}</SelectItem>}
            </SelectContent>
          </Select>
          <ExPicker opts={opts} value={ex} onChange={setEx} />
        </div>
        <span className="mono text-[11px] text-zinc-400" data-testid="c360-cmp-count">{count}</span>
      </div>
      <div className="flex items-baseline gap-3">
        <span className="mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">{av === "live" ? "Live crowd & engagement" : "Reach & engagement"}</span>
        <span className="text-[11px] text-zinc-500">Click a card to see where they stand</span>
      </div>
      <div className={av === "live" ? "grid grid-cols-1 lg:grid-cols-[4fr_1fr] gap-3" : ""}>
        {parts.map(([part, label]) => (
          <div key={label || "all"}>
            {label && <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>}
            <div className={`grid gap-2 ${part === "week" ? "grid-cols-2 lg:grid-cols-1" : av === "live" ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 lg:grid-cols-5"}`}>
              {cards.filter((c) => c.part === part).map((c) => <Card key={c.k} c={c} av={av} on={c.k === card.k} onPick={() => setSel(c.k)} />)}
            </div>
          </div>
        ))}
      </div>
      <Panel c={card} av={av} d={d} pick={pick} typ={typ} />
    </section>
  );
}
